import dayjs from 'dayjs';

import { Client, DiscountMode, Invoice, InvoiceDraft, InvoicingSummary, LineItem, PaymentMethod, Terms } from './types';

export const DEFAULT_MESSAGE = 'Thank you for your business.';

export const DEFAULT_PAYMENT_METHODS: PaymentMethod[] = ['ach', 'card'];

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
    ach: 'Bank Account (ACH)',
    card: 'Credit Card',
    cash_or_check: 'Cash or Check',
    saved_method: 'Charge Saved Payment Method',
};

const TERMS_DAYS: Record<Terms, number> = { due_now: 0, due_10: 10, due_15: 15, due_30: 30 };

export const currencyShort = (value: number) => {
    if (Math.abs(value) >= 1000) {
        return `$${Math.round(value / 1000)}K`;
    }
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(
        value
    );
};

export const currencyExact = (value: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(value);

export const lineAmount = (item: Pick<LineItem, 'quantity' | 'price'>) => item.quantity * item.price;

export function computeTotals(items: LineItem[], discount: string, mode: DiscountMode) {
    const subtotal = items.reduce((sum, item) => sum + lineAmount(item), 0);
    const raw = Number(discount);
    const value = Number.isFinite(raw) && raw > 0 ? raw : 0;
    const discountAmount = Math.min(subtotal, mode === 'pct' ? (subtotal * Math.min(value, 100)) / 100 : value);
    return { subtotal, discountAmount, total: subtotal - discountAmount };
}

/** How the panel shows the discount: as the percentage entered, or as the dollar amount taken off. */
export const discountLabel = (mode: DiscountMode, discount: string, discountAmount: number) =>
    mode === 'pct' ? `${(Number(discount) || 0).toFixed(2)}%` : currencyExact(discountAmount);

export const dueDateFor = (date: string, terms: Terms) =>
    dayjs(date).add(TERMS_DAYS[terms], 'day').format('YYYY-MM-DD');

/** "Acme Consulting" + 38 existing → "ACM-039", the way the real tool prefixes from the business name. */
export function nextInvoiceNumber(businessName: string, invoices: Invoice[]): string {
    const prefix =
        businessName
            .replace(/[^a-z0-9]/gi, '')
            .slice(0, 3)
            .toUpperCase() || 'INV';
    const highest = invoices.reduce((max, inv) => Math.max(max, Number(/(\d+)$/.exec(inv.number)?.[1] ?? 0)), 0);
    return `${prefix}-${String(highest + 1).padStart(3, '0')}`;
}

export const initials = (name: string) =>
    name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => word[0].toUpperCase())
        .join('');

export function newDraft(number: string): InvoiceDraft {
    return {
        clientId: '',
        clientEmail: '',
        cc: '',
        bcc: '',
        number,
        date: dayjs().format('YYYY-MM-DD'),
        terms: 'due_now',
        frequency: 'one_time',
        reminders: false,
        discount: '',
        discountMode: 'usd',
        message: DEFAULT_MESSAGE,
        items: [],
    };
}

/** Adds the session's new invoices onto the seeded summary, so sending one moves the overview figures. */
export function withCreated(summary: InvoicingSummary, created: Invoice[]): InvoicingSummary {
    return created.reduce(
        (acc, inv) =>
            inv.status === 'draft'
                ? acc
                : {
                      ...acc,
                      total_invoiced: acc.total_invoiced + inv.amount,
                      total_outstanding: acc.total_outstanding + inv.amount,
                      unpaid_count: acc.unpaid_count + 1,
                  },
        summary
    );
}

/**
 * Seeded invoices carry only a total. Fill in the rest from the client so the detail view reads
 * like a real invoice: one line from the client's usual item, priced at the invoice total.
 */
export function invoiceDetails(invoice: Invoice, clients: Client[]) {
    const client = clients.find((c) => c.name === invoice.client);
    return {
        clientEmail: invoice.client_email ?? client?.email ?? '',
        items: invoice.items ?? [
            {
                id: `${invoice.id}_item`,
                description: client?.default_item?.description ?? 'Professional services',
                quantity: 1,
                price: invoice.amount,
            },
        ],
        discount: invoice.discount ?? 0,
        discountLabel: invoice.discount_label ?? currencyExact(invoice.discount ?? 0),
        message: invoice.message ?? DEFAULT_MESSAGE,
        paymentMethods: invoice.payment_methods ?? DEFAULT_PAYMENT_METHODS,
    };
}
